import React from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { UserProfileCard } from "@/components/jartinet/UserProfileCard";
import { Sidebar, SidebarContent, SidebarFooter, SidebarHeader, SidebarInset, SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { JartilandLogo } from "@/components/icons/JartilandLogo";
import { Home, Shield, Users, Newspaper, UserCog, Megaphone, Music, Calendar, DollarSign, Package, Landmark, Mailbox, BookOpen, Menu, TrendingUp, Layers, PenLine, Dice5, ShoppingBag, Sparkles, Trophy, Gem, Contact, Disc3 } from 'lucide-react';
import { SidebarMenu, SidebarMenuItem, SidebarMenuButton, SidebarGroup, SidebarGroupLabel } from "@/components/ui/sidebar";
import { useAuth } from '@/hooks/useAuth';
import { useEffect, useState } from "react";
import { collection, query, where, onSnapshot, getDocs, getDoc, doc } from 'firebase/firestore';
import { db } from '@/firebase/config';
import { Organizacion } from "@/lib/types";
import { getNotificationCounts, markSectionSeen, type NotificationCounts } from '@/lib/actions';
import { Loader2 } from 'lucide-react';
import { TransitionLink } from '@/providers/TransitionProvider';
import { cn } from '@/lib/utils';
import { motion } from 'framer-motion';

/** Cada cuánto se piden los contadores del sidebar (ver el efecto de abajo). */
const NOTIF_POLL_MS = 5 * 60 * 1000;

const PageLoader = () => (
  <div className="fixed inset-0 z-[100] flex items-center justify-center bg-background">
    <Loader2 className="h-12 w-12 animate-spin text-primary" />
  </div>
);

function NavItem({ to, icon: Icon, label, isActive, badge }: {
  to: string;
  icon: React.ElementType;
  label: string;
  isActive: boolean;
  badge?: number;
}) {
  return (
    <SidebarMenuItem>
      <TransitionLink to={to} className="w-full">
        <SidebarMenuButton
          isActive={isActive}
          className={cn(
            "group relative overflow-hidden rounded-xl transition-all duration-200 h-9",
            isActive
              ? "bg-gradient-to-r from-primary/20 via-primary/10 to-transparent text-primary font-semibold border border-primary/25 shadow-[0_0_16px_rgba(250,204,21,0.12)]"
              : "hover:bg-white/5 hover:translate-x-0.5 text-muted-foreground hover:text-foreground"
          )}
        >
          {isActive && (
            <motion.span
              layoutId="jartinet-sidebar-active"
              className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 rounded-r-full bg-gradient-to-b from-primary to-amber-500 shadow-[0_0_8px_rgba(250,204,21,0.6)]"
              transition={{ type: 'spring', stiffness: 380, damping: 30 }}
            />
          )}
          <Icon className={cn("h-4 w-4 flex-shrink-0 transition-transform duration-200", isActive ? "text-primary" : "group-hover:scale-110 group-hover:text-primary")} />
          <span className="flex-1 truncate">{label}</span>
          {badge != null && badge > 0 && (
            <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-gradient-to-br from-primary to-amber-500 px-1 text-[10px] font-bold text-primary-foreground shadow-sm">
              {badge}
            </span>
          )}
        </SidebarMenuButton>
      </TransitionLink>
    </SidebarMenuItem>
  );
}

export default function JartinetLayout() {
  const { user, userData, loading, jartinetData } = useAuth();
  const location = useLocation();
  const pathname = location.pathname;
  const [isArzobispadoAuthorized, setIsArzobispadoAuthorized] = useState(false);
  const [isPeriodicoEditor, setIsPeriodicoEditor] = useState(false);
  const [unreadLettersCount, setUnreadLettersCount] = useState(0);
  const [notif, setNotif] = useState<NotificationCounts>({ social: 0, avisos: 0, periodico: 0, mod: 0, banco: 0, hermandades: 0, bandas: 0, tienda: 0 });

  const isAdmin = userData?.role === 'admin';

  // JartiMarchas es para DIRECTORES de banda (y admins). Sale de `jartinetData`,
  // que ya está en memoria desde el login: ni una lectura extra ni un spinner.
  const dirigeAlgunaBanda = React.useMemo(
    () => isAdmin || !!(user && (jartinetData?.bandas || []).some(
      (b) => Array.isArray(b.directores) && b.directores.includes(user.uid))),
    [isAdmin, user, jartinetData],
  );

  // ── Notificaciones: contadores por sección (poll + refresco al navegar) ──
  const refreshNotif = React.useCallback(async () => {
    if (!user) return;
    const res = await getNotificationCounts();
    if (res.success) setNotif(res.counts);
  }, [user]);

  // El sondeo va a 5 MINUTOS y se PARA con la pestaña en segundo plano. Antes
  // eran 45 s sin parar nunca: una pestaña abierta toda la tarde son cientos de
  // llamadas, cada una con varias consultas a Firestore detrás. Al volver a la
  // pestaña se refresca al momento, así que no se nota la espera.
  useEffect(() => {
    if (!user) return;
    let id: ReturnType<typeof setInterval> | null = null;

    const arranca = () => {
      if (id !== null) return;
      id = setInterval(refreshNotif, NOTIF_POLL_MS);
    };
    const para = () => {
      if (id === null) return;
      clearInterval(id);
      id = null;
    };
    const alCambiarVisibilidad = () => {
      if (document.visibilityState === 'visible') { refreshNotif(); arranca(); }
      else para();
    };

    refreshNotif();
    if (document.visibilityState === 'visible') arranca();
    document.addEventListener('visibilitychange', alCambiarVisibilidad);
    return () => {
      document.removeEventListener('visibilitychange', alCambiarVisibilidad);
      para();
    };
  }, [user, refreshNotif]);

  // Al entrar en una sección, la marca como vista y refresca contadores.
  useEffect(() => {
    if (!user) return;
    const sectionByPath: Record<string, string> = {
      '/jartinet/social': 'social',
      '/jartinet/avisos': 'avisos',
      '/jartinet/periodico': 'periodico',
      '/jartinet/mod-cofrade': 'mod',
      '/jartinet/banco': 'banco',
      '/jartinet/hermandades': 'hermandades',
      '/jartinet/bandas': 'bandas',
    };
    const match = Object.keys(sectionByPath).find((p) => pathname === p || pathname.startsWith(p + '/'));
    if (match) {
      markSectionSeen(sectionByPath[match]).then(() => {
        setNotif((prev) => ({ ...prev, [sectionByPath[match]]: 0 }));
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, user]);

  useEffect(() => {
    if (!user) return;
    const q = query(collection(db, 'users', user.uid, 'cartas'), where('isRead', '==', false));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setUnreadLettersCount(snapshot.size);
    });
    return () => unsubscribe();
  }, [user]);

  useEffect(() => {
    if (!user || !userData) { setIsArzobispadoAuthorized(false); return; }
    if (isAdmin) { setIsArzobispadoAuthorized(true); return; }
    (async () => {
      const q = query(collection(db, 'organizaciones'), where('nombreCompleto', '==', 'Arzobispado'));
      const snap = await getDocs(q);
      if (!snap.empty) {
        const data = snap.docs[0].data() as Organizacion;
        setIsArzobispadoAuthorized(Array.isArray(data.gestores) && data.gestores.includes(user.uid));
      } else {
        setIsArzobispadoAuthorized(false);
      }
    })();
  }, [user, userData, isAdmin]);

  useEffect(() => {
    if (!user || !userData) { setIsPeriodicoEditor(false); return; }
    if (isAdmin) { setIsPeriodicoEditor(true); return; }
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'app_settings', 'periodico'));
        const editores = (snap.exists() ? snap.data()?.editores : []) as string[] | undefined;
        setIsPeriodicoEditor(Array.isArray(editores) && editores.includes(user.uid));
      } catch { setIsPeriodicoEditor(false); }
    })();
  }, [user, userData, isAdmin]);

  if (loading) return <PageLoader />;

  return (
    <div className="relative">
      <SidebarProvider>
        <Sidebar className="border-r border-white/5">

          {/* ── Header ── */}
          <SidebarHeader className="relative overflow-hidden border-b border-white/5 py-4 px-4">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/15 via-transparent to-transparent pointer-events-none" />
            <div className="absolute -top-10 -left-10 h-32 w-32 rounded-full bg-primary/10 blur-2xl pointer-events-none" />
            <TransitionLink to="/" className="group relative flex items-center gap-3">
              <div className="relative flex-shrink-0">
                <div
                  className="absolute inset-0 blur-lg opacity-50 rounded-full scale-125 transition-opacity duration-300 group-hover:opacity-80"
                  style={{ backgroundColor: 'hsl(var(--primary))' }}
                />
                <div className="relative rounded-xl border border-primary/30 bg-primary/10 p-1.5 transition-transform duration-300 group-hover:scale-105">
                  <JartilandLogo />
                </div>
              </div>
              <div className="min-w-0">
                <span className="font-headline text-base font-bold leading-tight block">
                  <span className="bg-gradient-to-r from-primary to-amber-500 bg-clip-text text-transparent">Jartinet</span>
                </span>
                <span className="text-[0.62rem] text-muted-foreground/60 leading-none">by Jartiland</span>
              </div>
            </TransitionLink>
          </SidebarHeader>

          {/* ── Nav content ── */}
          <SidebarContent className="px-2 py-3">
            <SidebarMenu className="gap-0.5">

              {/* Top-level items */}
              <NavItem to="/jartinet/home"   icon={Home}  label="Panel Principal" isActive={pathname === '/jartinet/home'} />
              <NavItem to="/jartinet/social" icon={Users} label="Social"           isActive={pathname.startsWith('/jartinet/social')} badge={notif.social} />

              {/* Comunidad */}
              <SidebarGroup className="mt-2 px-0">
                <SidebarGroupLabel className="px-2 text-[0.62rem] font-bold uppercase tracking-widest text-primary/50 mb-1.5">
                  Comunidad
                </SidebarGroupLabel>
                <SidebarMenu className="gap-0.5">
                  <NavItem to="/jartinet/hermandades"  icon={Shield}   label="Hermandades" isActive={pathname === '/jartinet/hermandades'} badge={notif.hermandades} />
                  <NavItem to="/jartinet/bandas"       icon={Music}    label="Bandas"      isActive={pathname === '/jartinet/bandas'} badge={notif.bandas} />
                  <NavItem to="/jartinet/banco"        icon={Landmark} label="JartiBank"   isActive={pathname.startsWith('/jartinet/banco')} badge={notif.banco} />
                  <NavItem to="/jartinet/jartibet"     icon={Dice5}    label="JartiBet"    isActive={pathname === '/jartinet/jartibet'} />
                  <NavItem to="/jartinet/jarticards"   icon={Layers}   label="JartiCards"  isActive={pathname === '/jartinet/jarticards'} />
                  <NavItem to="/jartinet/buzon"        icon={Mailbox}  label="Buzón"       isActive={pathname === '/jartinet/buzon'} badge={unreadLettersCount} />
                  <NavItem to="/jartinet/hall-of-fame" icon={Trophy}   label="Hall of Fame" isActive={pathname === '/jartinet/hall-of-fame'} />
                  <NavItem to="/jartinet/carnet"       icon={Contact}  label="Mi carnet"   isActive={pathname.startsWith('/jartinet/carnet')} />
                </SidebarMenu>
              </SidebarGroup>

              {/* Contenido */}
              <SidebarGroup className="mt-2 px-0">
                <SidebarGroupLabel className="px-2 text-[0.62rem] font-bold uppercase tracking-widest text-primary/50 mb-1.5">
                  Contenido
                </SidebarGroupLabel>
                <SidebarMenu className="gap-0.5">
                  <NavItem to="/jartinet/avisos"              icon={Megaphone} label="Avisos"          isActive={pathname === '/jartinet/avisos'} badge={notif.avisos} />
                  <NavItem to="/jartinet/periodico"           icon={Newspaper} label="Periódico"       isActive={pathname === '/jartinet/periodico'} badge={notif.periodico} />
                  {isPeriodicoEditor && (
                    <NavItem to="/jartinet/redaccion"         icon={PenLine}   label="Redacción"       isActive={pathname === '/jartinet/redaccion'} />
                  )}
                  <NavItem to="/jartinet/calendario"          icon={Calendar}  label="Calendario"      isActive={pathname === '/jartinet/calendario'} />
                  <NavItem to="/jartinet/programa-semana-santa" icon={BookOpen} label="Programa S. Santa" isActive={pathname === '/jartinet/programa-semana-santa'} />
                </SidebarMenu>
              </SidebarGroup>

              {/* Servicios */}
              <SidebarGroup className="mt-2 px-0">
                <SidebarGroupLabel className="px-2 text-[0.62rem] font-bold uppercase tracking-widest text-primary/50 mb-1.5">
                  Servicios
                </SidebarGroupLabel>
                <SidebarMenu className="gap-0.5">
                  <NavItem to="/jartinet/mod-cofrade"          icon={Package}      label="Mod Cofrade"     isActive={pathname === '/jartinet/mod-cofrade'} badge={notif.mod} />
                  <NavItem to="/jartinet/comprobar-shaders"    icon={Sparkles}     label="Comprobar Shaders" isActive={pathname === '/jartinet/comprobar-shaders'} />
                  <NavItem to="/jartinet/eljartiingles"        icon={ShoppingBag}  label="El Jarti Inglés" isActive={pathname.startsWith('/jartinet/eljartiingles')} />
                  <NavItem to="/jartinet/personalizables"      icon={Gem}          label="Personalizables" isActive={pathname.startsWith('/jartinet/personalizables')} badge={notif.tienda} />
                  {dirigeAlgunaBanda && (
                    <NavItem to="/jartinet/jartimarchas"      icon={Disc3}        label="JartiMarchas"    isActive={pathname.startsWith('/jartinet/jartimarchas')} />
                  )}
                </SidebarMenu>
              </SidebarGroup>

              {/* Arzobispado */}
              {isArzobispadoAuthorized && (
                <SidebarGroup className="mt-2 px-0">
                  <SidebarGroupLabel className="px-2 text-[0.62rem] font-bold uppercase tracking-widest text-primary/50 mb-1.5">
                    Arzobispado
                  </SidebarGroupLabel>
                  <SidebarMenu className="gap-0.5">
                    <NavItem to="/jartinet/arzobispado" icon={Landmark} label="Arzobispado" isActive={pathname === '/jartinet/arzobispado'} />
                  </SidebarMenu>
                </SidebarGroup>
              )}

              {/* Organizaciones */}
              {(jartinetData?.organizaciones?.filter(org => (Array.isArray(org.gestores) && org.gestores.includes(user?.uid)) || isAdmin).length || 0) > 0 && (
                <SidebarGroup className="mt-2 px-0">
                  <SidebarGroupLabel className="px-2 text-[0.62rem] font-bold uppercase tracking-widest text-primary/50 mb-1.5">
                    Gestión de Cuentas
                  </SidebarGroupLabel>
                  <SidebarMenu className="gap-0.5">
                    <NavItem to="/jartinet/organizaciones" icon={Landmark} label="Organizaciones" isActive={pathname === '/jartinet/organizaciones'} />
                  </SidebarMenu>
                </SidebarGroup>
              )}

              {/* Admin */}
              {isAdmin && (
                <SidebarGroup className="mt-2 px-0">
                  <SidebarGroupLabel className="px-2 text-[0.62rem] font-bold uppercase tracking-widest text-primary/50 mb-1.5">
                    Administración
                  </SidebarGroupLabel>
                  <SidebarMenu className="gap-0.5">
                    <NavItem to="/jartinet/admin/home" icon={UserCog} label="Panel de Admin" isActive={pathname.startsWith('/jartinet/admin')} />
                  </SidebarMenu>
                </SidebarGroup>
              )}

            </SidebarMenu>
          </SidebarContent>

          {/* ── Footer ── */}
          <SidebarFooter className="border-t border-white/5 px-2 py-3">
            <UserProfileCard />
          </SidebarFooter>

        </Sidebar>

        <SidebarInset>
          <header className="sticky top-0 z-40 flex h-14 items-center gap-4 border-b border-white/5 bg-background/80 px-4 backdrop-blur-md supports-[backdrop-filter]:bg-background/60 md:hidden">
            <SidebarTrigger>
              <Menu />
            </SidebarTrigger>
            <span className="font-headline text-base font-semibold">
              <span className="bg-gradient-to-r from-primary to-amber-500 bg-clip-text text-transparent">Jartinet</span>
            </span>
          </header>
          <div className="relative p-4 md:p-6 lg:p-8">
            <Outlet />
          </div>
        </SidebarInset>
      </SidebarProvider>
    </div>
  );
}
